(async () => {
  const { createQuickAdd } = await import('@theme/vspots-card-quick-add');
  const positionStorageKey = "vspots_geo_position_v3";
  const manualLocationStorageKey = "vspots_geo_manual_location_v2";
  const positionLifetime = 7 * 24 * 60 * 60 * 1000;
  const manualLocationLifetime = 30 * 24 * 60 * 60 * 1000;
  const requestTimeout = 8000;
  const rootSelector = "product-recommendations[data-vspots-related-deals]";
  const rootRequests = new WeakMap();
  const rootSliderCleanups = new WeakMap();
  const observedRoots = new WeakSet();
  let viewportObserver = null;
  const categoryNames = {
    activity: "Activity",
    "freizeit-action": "Activity",
    "freizeit-und-action": "Activity",
    beauty: "Beauty",
    fitness: "Fitness",
    sport: "Fitness",
    food: "Food",
    essen: "Food",
    "essen-trinken": "Food",
    "essen-und-trinken": "Food",
    services: "Services",
    service: "Services",
    dienstleistungen: "Services",
    wellness: "Wellness",
  };

  function parseStoredValue(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key));
    } catch {
      return null;
    }
  }

  function readStoredLocation() {
    const position = parseStoredValue(positionStorageKey);
    if (
      validLocation(position) &&
      Number.isFinite(position?.createdAt) &&
      Date.now() - position.createdAt < positionLifetime
    ) {
      return position;
    }

    const manualLocation = parseStoredValue(manualLocationStorageKey);
    if (
      validLocation(manualLocation) &&
      Number.isFinite(manualLocation?.createdAt) &&
      Date.now() - manualLocation.createdAt < manualLocationLifetime
    ) {
      return manualLocation;
    }

    return null;
  }

  function validLocation(value) {
    return (
      Number.isFinite(value?.latitude) &&
      Number.isFinite(value?.longitude) &&
      value.latitude >= -90 &&
      value.latitude <= 90 &&
      value.longitude >= -180 &&
      value.longitude <= 180 &&
      (value.latitude !== 0 || value.longitude !== 0)
    );
  }

  function validDealCoordinates(latitude, longitude) {
    return (
      validLocation({ latitude, longitude }) &&
      latitude >= 34 &&
      latitude <= 72 &&
      longitude >= -25 &&
      longitude <= 45
    );
  }

  function distanceBetweenPositions(origin, destination) {
    const earthRadiusMeters = 6371000;
    const toRadians = (degrees) => (degrees * Math.PI) / 180;
    const latitudeDelta = toRadians(
      destination.latitude - origin.latitude,
    );
    const longitudeDelta = toRadians(
      destination.longitude - origin.longitude,
    );
    const originLatitude = toRadians(origin.latitude);
    const destinationLatitude = toRadians(destination.latitude);
    const haversine =
      Math.sin(latitudeDelta / 2) ** 2 +
      Math.cos(originLatitude) *
        Math.cos(destinationLatitude) *
        Math.sin(longitudeDelta / 2) ** 2;

    return (
      earthRadiusMeters *
      2 *
      Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
    );
  }

  function nearestDealDistance(deal, location) {
    if (!validLocation(location) || !Array.isArray(deal?.location_points))
      return Number.POSITIVE_INFINITY;

    return deal.location_points.reduce((nearestDistance, point) => {
      if (!Array.isArray(point) || point.length !== 2) return nearestDistance;
      const latitude = Number(point[0]);
      const longitude = Number(point[1]);
      if (!validDealCoordinates(latitude, longitude)) return nearestDistance;

      return Math.min(
        nearestDistance,
        distanceBetweenPositions(location, { latitude, longitude }),
      );
    }, Number.POSITIVE_INFINITY);
  }

  function normalizeProductId(value) {
    return String(value || "")
      .split("/")
      .pop()
      .trim();
  }

  function relatedLimit(root) {
    const limit = Number(root.dataset.vspotsRelatedLimit);
    return Number.isSafeInteger(limit) && limit > 0 ? Math.min(limit, 12) : 3;
  }

  function normalizeCategory(value) {
    return String(value || "")
      .trim()
      .toLocaleLowerCase("de-DE")
      .replaceAll(/[^a-z0-9]+/gu, "-")
      .replace(/^-|-$/gu, "");
  }

  function apiCategory(value) {
    const normalized = normalizeCategory(value);
    return categoryNames[normalized] || value;
  }

  function categoryMatches(candidate, expected) {
    return (
      apiCategory(candidate).toLocaleLowerCase("de-DE") ===
      apiCategory(expected).toLocaleLowerCase("de-DE")
    );
  }

  function primaryCity(value) {
    return String(value || "").split(",")[0].trim();
  }

  function cityMatches(candidate, expected) {
    return (
      primaryCity(candidate).toLocaleLowerCase("de-DE") ===
      primaryCity(expected).toLocaleLowerCase("de-DE")
    );
  }

  function createElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = text;
    return element;
  }

  function formatMoney(root, cents) {
    return new Intl.NumberFormat(document.documentElement.lang || "de-DE", {
      style: "currency",
      currency: root.dataset.vspotsCurrency || "EUR",
    }).format((Number(cents) || 0) / 100);
  }

  function formatDistance(meters) {
    if (!Number.isFinite(meters)) return "";
    if (meters < 1000)
      return `${Math.max(100, Math.round(meters / 100) * 100)} m`;

    const kilometers = meters / 1000;
    return `${new Intl.NumberFormat(document.documentElement.lang || "de-DE", {
      maximumFractionDigits: kilometers < 10 ? 1 : 0,
    }).format(kilometers)} km`;
  }

  function badgeKey(value) {
    return String(value)
      .normalize("NFKD")
      .replace(/\p{Mark}/gu, "")
      .toLocaleLowerCase("de-DE")
      .replace(/[^a-z0-9]+/gu, "-")
      .replace(/^-|-$/gu, "");
  }

  function badgeLabel(key, fallback) {
    const labels = {
      topseller: "Meistverkauft",
      gift: "Perfekt als Geschenk",
      few: "Fast ausverkauft",
      expiring: "Endet bald",
      new: "Neu",
      "top-rated": "Top bewertet",
      "family-hit": "Familien-Hit",
      "for-two": "Für 2",
    };
    const label = String(labels[key] || fallback).trim();
    return label
      ? `${label.charAt(0).toLocaleUpperCase("de-DE")}${label.slice(1)}`
      : "";
  }

  function createEditorialBadges(values) {
    if (!Array.isArray(values)) return null;
    const labels = values
      .map((value) => String(value).trim())
      .filter(Boolean)
      .slice(0, 2);
    if (labels.length === 0) return null;

    const badges = createElement("div", "vspots-editorial-badges");
    badges.setAttribute("aria-label", "Deal-Auszeichnungen");
    labels.forEach((value) => {
      const key = badgeKey(value);
      const badge = createElement(
        "span",
        "vspots-editorial-badge",
        badgeLabel(key, value),
      );
      badge.dataset.vspotsBadge = key;
      badges.append(badge);
    });
    return badges;
  }

  function createFavoriteButton(deal) {
    const button = createElement(
      "button",
      "vspots-favorite-button vspots-favorite-button--card",
    );
    button.type = "button";
    button.dataset.vspotsFavoriteButton = "";
    button.dataset.vspotsFavoriteProductId = normalizeProductId(
      deal.shopify_product_id,
    );
    button.dataset.vspotsFavoriteTitle = deal.title || "";
    button.dataset.vspotsFavoriteUrl = deal.product_path || "";
    button.dataset.vspotsFavoriteImage = deal.image_url || "";
    button.dataset.vspotsFavoriteVendor = deal.vendor || "";
    button.setAttribute(
      "aria-label",
      document.body.dataset.vspotsFavoriteAddLabel || "Als Favorit speichern",
    );
    button.setAttribute("aria-pressed", "false");

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("aria-hidden", "true");
    const fill = document.createElementNS("http://www.w3.org/2000/svg", "path");
    fill.setAttribute("class", "vspots-favorite-button__fill");
    fill.setAttribute(
      "d",
      "M12 21s-8-4.9-8-11.2C4 6.6 6.2 4.5 9 4.5c1.5 0 2.6.7 3 1.5.4-.8 1.5-1.5 3-1.5 2.8 0 5 2.1 5 5.3C20 16.1 12 21 12 21Z",
    );
    fill.setAttribute("fill", "currentColor");
    const outline = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "path",
    );
    outline.setAttribute("d", fill.getAttribute("d"));
    outline.setAttribute("fill", "none");
    outline.setAttribute("stroke", "currentColor");
    outline.setAttribute("stroke-linecap", "round");
    outline.setAttribute("stroke-linejoin", "round");
    outline.setAttribute("stroke-width", "1.8");
    svg.append(fill, outline);
    button.append(svg);
    return button;
  }

  function createDealCard(root, result) {
    const { deal } = result;
    const wrapper = createElement("div", "resource-list__item");
    wrapper.dataset.vspotsRelatedCard = "";
    wrapper.dataset.vspotsRelatedCity = primaryCity(
      Array.isArray(deal.cities) ? deal.cities[0] : "",
    );

    const card = createElement("article", "vspots-deal-card vspots-deal-card--actionable");
    card.style.setProperty('--quick-add-display', root.dataset.vspotsQuickAddEnabled === 'true' ? 'flex' : 'none');
    card.style.setProperty('--quick-add-mobile-display', root.dataset.vspotsMobileQuickAddEnabled === 'true' ? 'flex' : 'none');
    card.style.setProperty('--quick-add-mobile-opacity', root.dataset.vspotsMobileQuickAddEnabled === 'true' ? '1' : '0');
    card.dataset.vspotsGeoCard = "";
    card.dataset.vspotsProductId = normalizeProductId(deal.shopify_product_id);
    card.dataset.vspotsCategory = normalizeCategory(deal.category);
    const firstPoint = Array.isArray(deal.location_points)
      ? deal.location_points.find(
          (point) =>
            Array.isArray(point) &&
            point.length === 2 &&
            validDealCoordinates(Number(point[0]), Number(point[1])),
        )
      : null;
    if (firstPoint) {
      card.dataset.vspotsLatitude = String(firstPoint[0]);
      card.dataset.vspotsLongitude = String(firstPoint[1]);
    }

    const link = createElement("a", "vspots-deal-card__link vspots-deal-card__body-link");
    link.href = deal.product_path;
    const media = createElement("div", "vspots-deal-card__media");
    if (deal.image_url) {
      const image = document.createElement("img");
      image.src = deal.image_url;
      image.alt = deal.image_alt || deal.title;
      image.loading = "lazy";
      image.decoding = "async";
      media.append(image);
    } else {
      const placeholder = createElement(
        "span",
        "vspots-deal-card__placeholder",
      );
      placeholder.setAttribute("aria-hidden", "true");
      media.append(placeholder);
    }

    const badges = createElement("div", "vspots-deal-card__badges");
    const city = Array.isArray(deal.cities) ? deal.cities[0] : "";
    if (city) badges.append(createElement("span", "", city));
    if (Number((deal.display_discount_percent ?? (Number(deal.variant_count) === 1 ? deal.discount_percent_max : 0))) > 0) {
      badges.append(
        createElement("span", "is-discount", `-${(deal.display_discount_percent ?? (Number(deal.variant_count) === 1 ? deal.discount_percent_max : 0))}%`),
      );
    }
    media.append(badges);

    const formattedDistance = firstPoint
      ? formatDistance(result.distance_meters)
      : "";
    if (formattedDistance) {
      const distance = createElement(
        "span",
        "vspots-distance-badge",
        formattedDistance,
      );
      const distanceLabel =
        root.dataset.vspotsDistanceLabel || "__DISTANCE__ entfernt";
      distance.setAttribute(
        "aria-label",
        distanceLabel.replace("__DISTANCE__", formattedDistance),
      );
      media.classList.add("vspots-geo-media");
      media.append(distance);
    }

    const body = createElement("div", "vspots-deal-card__body");
    const editorialBadges = createEditorialBadges(deal.badges);
    if (editorialBadges) body.append(editorialBadges);
    body.append(createElement("h3", "", deal.title));
    const detailText = deal.redemption_period || deal.subtitle;
    if (detailText)
      body.append(createElement("p", "vspots-deal-card__subtitle", detailText));

    const price = createElement("div", "vspots-deal-card__price");
    if (
      Number((deal.display_compare_at_price_cents ?? (Number(deal.variant_count) === 1 ? deal.max_compare_at_price_cents : 0))) > Number(deal.min_price_cents)
    ) {
      price.append(
        createElement(
          "s",
          "",
          formatMoney(root, (deal.display_compare_at_price_cents ?? (Number(deal.variant_count) === 1 ? deal.max_compare_at_price_cents : 0))),
        ),
      );
    }
    const pricePrefix =
      Number(deal.max_price_cents) > Number(deal.min_price_cents)
        ? root.dataset.vspotsPriceFrom || "ab "
        : "";
    price.append(
      createElement(
        "strong",
        "",
        `${pricePrefix}${formatMoney(root, deal.min_price_cents)}`,
      ),
    );
    if (Number((deal.display_discount_percent ?? (Number(deal.variant_count) === 1 ? deal.discount_percent_max : 0))) > 0) {
      price.append(
        createElement(
          "span",
          "vspots-price-discount",
          `${(deal.display_discount_percent ?? (Number(deal.variant_count) === 1 ? deal.discount_percent_max : 0))} % Rabatt`,
        ),
      );
    }
    body.append(price);
    body.append(createElement("p", "vspots-deal-card__partner", deal.vendor));

    const mediaLink = createElement('a', 'vspots-deal-card__media-link');
    mediaLink.href = deal.product_path;
    mediaLink.setAttribute('aria-label', deal.title || 'Deal anzeigen');
    mediaLink.append(...media.childNodes);
    media.append(mediaLink);
    const quickAdd = createQuickAdd(root, deal);
    if (quickAdd) media.append(quickAdd);
    link.append(body);
    card.append(media, link, createFavoriteButton(deal));
    wrapper.append(card);
    return wrapper;
  }

  function buildSearchUrl(root, location) {
    const baseUrl = String(root.dataset.vspotsSearchApiUrl || "").trim();
    if (!baseUrl) return null;
    const url = new URL(
      "v1/deals/search",
      baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`,
    );
    url.searchParams.set("q", "");
    url.searchParams.set(
      "category",
      apiCategory(root.dataset.vspotsRelatedCategory),
    );
    url.searchParams.set("page", "1");
    const candidateCount = location
      ? Math.max(24, relatedLimit(root) + 1)
      : relatedLimit(root) + 1;
    url.searchParams.set(
      "perPage",
      String(candidateCount),
    );
    url.searchParams.set("sort", location ? "nearby" : "relevance");
    if (location) {
      url.searchParams.set("lat", String(location.latitude));
      url.searchParams.set("lng", String(location.longitude));
    } else {
      const city = primaryCity(root.dataset.vspotsRelatedCity);
      if (city) url.searchParams.set("city", city);
    }
    return url;
  }

  function sliderPageWidth(track) {
    const firstItem = track.querySelector(":scope > .resource-list__item");
    if (!firstItem) return track.clientWidth;
    const styles = window.getComputedStyle(track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    const itemWidth = firstItem.getBoundingClientRect().width;
    const visibleItems = Math.max(
      1,
      Math.floor((track.clientWidth + gap) / (itemWidth + gap)),
    );
    return visibleItems * (itemWidth + gap);
  }

  function updateSliderControls(root) {
    const track = root.querySelector("[data-vspots-related-track]");
    const controls = root.querySelector("[data-vspots-related-controls]");
    const previous = root.querySelector("[data-vspots-related-previous]");
    const next = root.querySelector("[data-vspots-related-next]");
    if (!track || !controls || !previous || !next) return;

    const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const hasOverflow = maximumScroll > 2;
    controls.hidden = !hasOverflow;
    previous.disabled = !hasOverflow || track.scrollLeft <= 2;
    next.disabled = !hasOverflow || track.scrollLeft >= maximumScroll - 2;
  }

  function bindSlider(root) {
    const track = root.querySelector("[data-vspots-related-track]");
    const previous = root.querySelector("[data-vspots-related-previous]");
    const next = root.querySelector("[data-vspots-related-next]");
    if (!track || !previous || !next) return;

    rootSliderCleanups.get(root)?.();
    const move = (direction) => {
      track.scrollBy({
        left: direction * sliderPageWidth(track),
        behavior: "smooth",
      });
    };
    const movePrevious = () => move(-1);
    const moveNext = () => move(1);
    const refresh = () =>
      window.requestAnimationFrame(() => updateSliderControls(root));
    const observer = new ResizeObserver(refresh);

    previous.addEventListener("click", movePrevious);
    next.addEventListener("click", moveNext);
    track.addEventListener("scroll", refresh, { passive: true });
    observer.observe(track);
    rootSliderCleanups.set(root, () => {
      previous.removeEventListener("click", movePrevious);
      next.removeEventListener("click", moveNext);
      track.removeEventListener("scroll", refresh);
      observer.disconnect();
    });
    track.scrollLeft = 0;
    refresh();
  }

  function fallback(root, location) {
    const grid = root.querySelector(".resource-list--grid");
    if (!grid) return;
    const expectedCategory = root.dataset.vspotsRelatedCategory;
    const expectedCity = primaryCity(root.dataset.vspotsRelatedCity);
    const requireMatchingCity = !validLocation(location) && expectedCity !== "";
    const items = Array.from(
      grid.querySelectorAll(":scope > [data-vspots-related-card]"),
    );
    const earthRadius = 6371;
    const toRadians = (degrees) => (degrees * Math.PI) / 180;

    const candidates = items
      .map((item, originalIndex) => {
        const card = item.querySelector("[data-vspots-geo-card]");
        if (
          !card ||
          !categoryMatches(card.dataset.vspotsCategory, expectedCategory)
        )
          return null;
        if (
          requireMatchingCity &&
          !cityMatches(item.dataset.vspotsRelatedCity, expectedCity)
        )
          return null;
        const latitudeValue = String(card.dataset.vspotsLatitude || "").trim();
        const longitudeValue = String(
          card.dataset.vspotsLongitude || "",
        ).trim();
        const latitude = Number(latitudeValue);
        const longitude = Number(longitudeValue);
        let distance = Number.POSITIVE_INFINITY;
        if (
          validLocation(location) &&
          latitudeValue !== "" &&
          longitudeValue !== "" &&
          validDealCoordinates(latitude, longitude)
        ) {
          const latitudeDelta = toRadians(latitude - location.latitude);
          const longitudeDelta = toRadians(longitude - location.longitude);
          const originLatitude = toRadians(location.latitude);
          const destinationLatitude = toRadians(latitude);
          const haversine =
            Math.sin(latitudeDelta / 2) ** 2 +
            Math.cos(originLatitude) *
              Math.cos(destinationLatitude) *
              Math.sin(longitudeDelta / 2) ** 2;
          distance =
            earthRadius *
            2 *
            Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
        }
        return { card, distance, item, originalIndex };
      })
      .filter(Boolean)
      .sort(
        (left, right) =>
          left.distance - right.distance ||
          left.originalIndex - right.originalIndex,
      );

    candidates.forEach(({ card, distance, item }) => {
      item.hidden = false;
      if (Number.isFinite(distance)) {
        const media = card.querySelector(".card-gallery, slideshow-component");
        if (media && !media.querySelector(":scope > .vspots-distance-badge")) {
          media.classList.add("vspots-geo-media");
          media.append(
            createElement(
              "span",
              "vspots-distance-badge",
              formatDistance(distance * 1000),
            ),
          );
        }
      }
      grid.append(item);
    });
    root.hidden = candidates.length === 0;
    bindSlider(root);
  }

  async function load(root, forcedLocation = undefined) {
    root.dataset.vspotsRelatedLoaded = "true";
    viewportObserver?.unobserve(root);
    const location =
      forcedLocation === undefined || !validLocation(forcedLocation)
        ? readStoredLocation()
        : forcedLocation;
    const url = buildSearchUrl(root, location);
    if (!url) {
      fallback(root, location);
      return;
    }

    rootRequests.get(root)?.abort();
    const request = new AbortController();
    rootRequests.set(root, request);
    const timeout = window.setTimeout(() => request.abort(), requestTimeout);
    try {
      const response = await window.fetch(url, {
        headers: { Accept: "application/json" },
        mode: "cors",
        signal: request.signal,
      });
      if (!response.ok)
        throw new Error(`Search API returned ${response.status}`);
      const body = await response.json();
      const currentProductId = normalizeProductId(
        root.dataset.vspotsRelatedProductId,
      );
      const expectedCategory = root.dataset.vspotsRelatedCategory;
      let results = (Array.isArray(body.results) ? body.results : []).filter(
        ({ deal }) =>
          deal?.available !== false &&
          normalizeProductId(deal?.shopify_product_id) !== currentProductId &&
          categoryMatches(deal?.category, expectedCategory),
      );

      if (validLocation(location)) {
        results = results
          .map((result) => ({
            ...result,
            distance_meters: nearestDealDistance(result.deal, location),
          }))
          .filter((result) => Number.isFinite(result.distance_meters))
          .sort(
            (left, right) => left.distance_meters - right.distance_meters,
          );
      }

      results = results.slice(0, relatedLimit(root));

      const grid = root.querySelector(".resource-list--grid");
      if (!grid || results.length === 0) {
        fallback(root, location);
        return;
      }
      const fragment = document.createDocumentFragment();
      results.forEach((result) =>
        fragment.append(createDealCard(root, result)),
      );
      grid.replaceChildren(fragment);
      grid.dataset.hasRecommendations = "true";
      root.hidden = false;
      bindSlider(root);
    } catch (error) {
      if (error.name !== "AbortError") fallback(root, location);
    } finally {
      window.clearTimeout(timeout);
      if (rootRequests.get(root) === request) rootRequests.delete(root);
    }
  }

  function loadNearViewport(root) {
    if (root.dataset.vspotsRelatedLoaded === "true" || observedRoots.has(root)) return;
    observedRoots.add(root);

    if (!("IntersectionObserver" in window)) {
      load(root);
      return;
    }

    viewportObserver ||= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) load(entry.target);
        });
      },
      { rootMargin: "700px 0px" },
    );
    viewportObserver.observe(root);
  }

  function initialize(scope = document) {
    scope.querySelectorAll(rootSelector).forEach(loadNearViewport);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener("shopify:section:load", (event) =>
    initialize(event.target),
  );
  window.addEventListener("vspots:location-changed", (event) => {
    document
      .querySelectorAll(rootSelector)
      .forEach((root) => {
        if (root.dataset.vspotsRelatedLoaded === "true") load(root, event.detail);
      });
  });
  window.addEventListener("storage", (event) => {
    if (
      event.key !== positionStorageKey &&
      event.key !== manualLocationStorageKey
    )
      return;
    document.querySelectorAll(rootSelector).forEach((root) => {
      if (root.dataset.vspotsRelatedLoaded === "true") load(root);
    });
  });
})();
